// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import config from '../config'
import { getFormattedDateTime2 } from '../utils'
// import { DateTime } from 'luxon'

import authSlice from '../store/slices/auth'
//dispatch(authSlice.actions.setLogout())

const baseQuery = fetchBaseQuery({
  baseUrl: `${config.rootPath}`,
  prepareHeaders: (headers, { getState }) => {
    // console.log('prepareHeaders!!!!! -> ', getState())
    const auth = getState().auth
    const token = (auth && auth.token) || null
    if (token) {
      headers.set('Authorization', `JWT ${token}`)
    }
    return headers
  },
})

const customBaseQuery = async (args, api, extraOptions) => {
  // console.log('customBaseQuery: ', args, api, extraOptions)
  let result = await baseQuery(args, api, extraOptions)
  if (result.error && result.error.status === 401) {
    console.log('Received unauthorized response, trying to refresh the token...')
    const auth = api.getState().auth
    const refreshToken = (auth && auth.refreshToken) || null
    const refreshResult = await baseQuery(
      {
        url: '/auth/token/refresh/',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          refresh: refreshToken,
        },
      },
      api,
      extraOptions,
    )
    if (refreshResult.data) {
      // store the new token
      api.dispatch(authSlice.actions.setAuthTokens(refreshResult.data))
      // retry the initial query
      result = await baseQuery(args, api, extraOptions)
    } else {
      console.log("Can't refresh auth token, logging out...")
      api.dispatch(authSlice.actions.setLogout())
    }
  }
  return result
}

// Define a service using a base URL and expected endpoints
export const cpApi = createApi({
  reducerPath: 'cpApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'cpUser',
    'Case',
    'Comment',
    'Solution',
    'PaytesterRecord',
    'Az7Record',
    'IbmRecord',
    'MonitoringHost',
    'MonitoringService',
    'MonitoringNetworkBandwidth',
    'MonitoringNetworkService',
    'MonitoringBackupInfo',
  ],
  endpoints: (builder) => ({
    requestCpUserAuthLink: builder.mutation({
      query: (body) => ({
        url: `/auth/login/`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }), //${name}`,
      //invalidatesTags: [{ type: 'cpUser', id: 'LIST' }],
      //providesTags: (result, error, id) => [{ type: 'cpUser', id }],
    }),
    getCpUserByToken: builder.query({
      query: (token) => {
        const headers = { 'Content-Type': 'application/json' }
        if (token) headers.Authorization = 'JWT ' + token

        return {
          url: `/auth/user/get_user/`,
          method: 'GET',
          headers,
        }
      }, //${name}`,
      //invalidatesTags: [{ type: 'cpUser', id: 'LIST' }],
      providesTags: (result, error, id) => [{ type: 'cpUser', id }],
    }),
    getCases: builder.query({
      query: (openOnly) => ({
        url: `/support/cases?open=${openOnly}`,
        method: 'GET',
        //validateStatus: (response, result) => response.status === 200 || response.status === 401,
      }), //${name}`,
      transformResponse: (response, meta, arg) =>
        response.map((x) => ({
          ...x,
          date_created: getFormattedDateTime2(new Date(x.date_created)),
        })),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Case', id })), { type: 'Case', id: 'LIST' }]
          : [{ type: 'Case', id: 'LIST' }],
      //invalidatesTags: [{ type: 'cpUser', id: 'LIST' }],
      //providesTags: (result, error, id) => [{ type: 'Case', id }],
    }),
    getCase: builder.query({
      query: (caseId) => ({
        url: `/support/cases/${caseId}`,
        method: 'GET',
      }),
      providesTags: (result, error, caseId) => [{ type: 'Case', caseId }],
      //invalidatesTags: [{ type: 'cpUser', id: 'LIST' }],
      //providesTags: (result, error, id) => [{ type: 'Case', id }],
    }),
    createCase: builder.mutation({
      query: (body) => ({
        url: `/support/cases/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Case', id: 'LIST' }],
    }),
    attachToCase: builder.mutation({
      query: (data) => ({
        url: `/support/cases/${data.caseId}/attach/`,
        method: 'POST',
        body: data.formData,
      }),
      //invalidatesTags: [{ type: 'Case', id: 'LIST' }],
    }),
    getCaseComments: builder.query({
      query: (caseId) => ({
        url: `/support/cases/${caseId}/comments`,
        method: 'GET',
      }), //${name}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Comment', id })), { type: 'Comment', id: 'LIST' }]
          : [{ type: 'Comment', id: 'LIST' }],
      //invalidatesTags: [{ type: 'cpUser', id: 'LIST' }],
      //providesTags: (result, error, id) => [{ type: 'Case', id }],
    }),
    createCaseComment: builder.mutation({
      query: (data) => ({
        url: `/support/cases/${data.case_id}/comments/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Comment', id: 'LIST' }],
    }),
    getSolutions: builder.query({
      query: () => ({
        url: `/support/solutions`,
        method: 'GET',
      }), //${name}`,
      keepUnusedDataFor: 3600,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Solution', id })),
              { type: 'Solution', id: 'LIST' },
            ]
          : [{ type: 'Solution', id: 'LIST' }],
      //invalidatesTags: [{ type: 'cpUser', id: 'LIST' }],
      //providesTags: (result, error, id) => [{ type: 'Case', id }],
    }),
    createPaytesterRecord: builder.mutation({
      query: (body) => ({
        url: `/assessment/paytester/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'PaytesterRecord', id: 'LIST' }],
    }),
    createAz7Record: builder.mutation({
      query: (body) => ({
        url: `/assessment/az7/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Az7Record', id: 'LIST' }],
    }),
    createIbmRecord: builder.mutation({
      query: (body) => ({
        url: `/assessment/ibm/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'IbmRecord', id: 'LIST' }],
    }),
    getMonitoringHosts: builder.query({
      query: () => ({
        url: `/support/monitoring/hosts`,
        method: 'GET',
        //validateStatus: (response, result) => response.status === 200 || response.status === 401,
      }),
      // transformResponse: (response, meta, arg) =>
      //   response.map((x) => ({
      //     ...x,
      //     date_created: getFormattedDateTime2(new Date(x.date_created)),
      //   })),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'MonitoringHost', id })),
              { type: 'MonitoringHost', id: 'LIST' },
            ]
          : [{ type: 'MonitoringHost', id: 'LIST' }],
    }),
    getMonitoringServices: builder.query({
      query: () => ({
        url: `/support/monitoring/services`,
        method: 'GET',
        //validateStatus: (response, result) => response.status === 200 || response.status === 401,
      }),
      // transformResponse: (response, meta, arg) =>
      //   response.map((x) => ({
      //     ...x,
      //     date_created: getFormattedDateTime2(new Date(x.date_created)),
      //   })),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'MonitoringService', id })),
              { type: 'MonitoringService', id: 'LIST' },
            ]
          : [{ type: 'MonitoringService', id: 'LIST' }],
    }),
    getMonitoringNetworkBandwidth: builder.query({
      query: (service_id) => ({
        url: `/support/monitoring/services/${service_id}/bandwidth/`,
        method: 'GET',
      }),
      transformResponse: (response, meta, arg) => {
        let result = {}
        if (response.bandwidth_entries !== undefined)
          result.bandwidth_entries = response.bandwidth_entries.map((x) => ({
            traffic_in: parseFloat(x.traffic_in),
            traffic_out: parseFloat(x.traffic_out),
            timestamp: parseInt(x.timestamp),
            //DateTime.fromSeconds(parseInt(x.timestamp)), //new Date(parseInt(x.timestamp)),
          }))
        if (response.traffic_in_max !== undefined) {
          result.traffic_in_max = response.traffic_in_max
          // if (result.bandwidth_entries !== undefined)
          //   result.traffic_in_max = [
          //     {
          //       timestamp: result.bandwidth_entries[0].timestamp,
          //       traffic_in_max: response.traffic_in_max,
          //     },
          //     {
          //       timestamp: result.bandwidth_entries[result.bandwidth_entries.length - 1].timestamp,
          //       traffic_in_max: response.traffic_in_max,
          //     },
          //   ]
          // else result.traffic_in_max = [{ timestamp: 0, traffic_in_max: response.traffic_in_max }]
        }

        if (response.traffic_out_max !== undefined) {
          result.traffic_out_max = response.traffic_out_max
          // if (result.bandwidth_entries !== undefined) {
          //   result.traffic_out_max = []
          //   for (let i = 0; i < result.bandwidth_entries.length; i++) {
          //     result.traffic_out_max.push({
          //       timestamp: result.bandwidth_entries[i].timestamp,
          //       traffic_out_max: response.traffic_out_max,
          //     })
          //   }
          // } else
          //   result.traffic_out_max = [{ timestamp: 0, traffic_out_max: response.traffic_out_max }]
        }

        return result
      },
      providesTags: (result) =>
        result
          ? [
              ...result.bandwidth_entries.map(({ id }) => ({
                type: 'MonitoringNetworkBandwidth',
                id,
              })),
              {
                type: 'MonitoringNetworkBandwidth',
                id: 'LIST',
              },
            ]
          : [{ type: 'MonitoringNetworkBandwidth', id: 'LIST' }],
    }),
    getMonitoringNetworkServices: builder.query({
      query: () => ({
        url: `/support/monitoring/netservices`,
        method: 'GET',
      }),
      keepUnusedDataFor: 3600,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'MonitoringNetworkService', id })),
              { type: 'MonitoringNetworkService', id: 'LIST' },
            ]
          : [{ type: 'MonitoringNetworkService', id: 'LIST' }],
    }),
    getMonitoringBackupInfo: builder.query({
      query: (service_id) => ({
        url: `/support/monitoring/services/${service_id}/backup/`,
        method: 'GET',
      }),
      transformResponse: (response, meta, arg) => {
        const result = response.map((x) => ({
          pool_size: (parseFloat(x.pool_size) / 1024 ** 3).toFixed(2), //Gb //1073741824,
          raw_storage: (parseFloat(x.raw_storage) / 1024 ** 3).toFixed(2), //Gb //1073741824,
          timestamp: parseInt(x.timestamp),
          //DateTime.fromSeconds(parseInt(x.timestamp)), //new Date(parseInt(x.timestamp)),
        }))

        return result
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'MonitoringBackupInfo',
                id,
              })),
              {
                type: 'MonitoringBackupInfo',
                id: 'LIST',
              },
            ]
          : [{ type: 'MonitoringBackupInfo', id: 'LIST' }],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useRequestCpUserAuthLinkMutation,
  useGetCpUserByTokenQuery,
  useGetCasesQuery,
  useGetCaseQuery,
  useCreateCaseMutation,
  useAttachToCaseMutation,
  useGetCaseCommentsQuery,
  useCreateCaseCommentMutation,
  useGetSolutionsQuery,
  useCreatePaytesterRecordMutation,
  useCreateAz7RecordMutation,
  useCreateIbmRecordMutation,
  useGetMonitoringHostsQuery,
  useGetMonitoringServicesQuery,
  useGetMonitoringNetworkServicesQuery,
  useGetMonitoringNetworkBandwidthQuery,
  useGetMonitoringBackupInfoQuery,
} = cpApi
